import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { useBasePath } from '@/lib/paths'
import { BusinessDetailed, BusinessAddress, BusinessContactInfo, BusinessActivity } from '@/types'
import { Building2, FileText, Users, ClipboardCheck, Plus, Pencil, Trash2, ChevronRight, Settings, Mail, Shield, MapPin, PieChart, Package } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EditBusinessSheet } from './sheets/EditBusinessSheet'
import { BusinessAddressSheet } from '../../components/businesses/BusinessAddressSheet'
import { BusinessContactInfoSheet } from '../../components/businesses/BusinessContactInfoSheet'
import { BusinessCountryOperationsSheet } from '../../components/businesses/BusinessCountryOperationsSheet'
import { BusinessActivitySheet } from '../../components/businesses/BusinessActivitySheet'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { StageBadge } from '@/components/shared/StageBadge'


type Props = {
  business: BusinessDetailed
  countries: { id: string; code: string; name: string }[]
  states: { id: string; code: string; name: string; country_id: string }[]
}

export default function BusinessesShow({ business, countries, states }: Props) {
  const basePath = useBasePath()
  const confirm = useConfirm()
  const [editOpen, setEditOpen] = useState(false)

  const [addressSheetOpen, setAddressSheetOpen] = useState(false)
  const [editAddress, setEditAddress] = useState<BusinessAddress | null>(null)

  const [contactInfoSheetOpen, setContactInfoSheetOpen] = useState(false)
  const [editContactInfo, setEditContactInfo] = useState<BusinessContactInfo | null>(null)

  const [countryOpsSheetOpen, setCountryOpsSheetOpen] = useState(false)

  const [activitySheetOpen, setActivitySheetOpen] = useState(false)
  const [editActivity, setEditActivity] = useState<BusinessActivity | null>(null)

  async function handleDelete() {
    const ok = await confirm({
      title: 'Delete Business',
      description: `Delete "${business.name}"? This will also delete all associated services.`,
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (ok) {
      router.delete(`${basePath}/businesses/${business.id}`)
    }
  }

  return (
    <DashboardLayout
      title={business.name}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: business.name }]}
    >
      <EditBusinessSheet business={business} open={editOpen} onOpenChange={setEditOpen} />

      <div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">{business.name}</h1>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setEditOpen(true)}>
                  <Pencil className="w-3.5 h-3.5 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href={`${basePath}/businesses/${business.id}/officers`}>
                    <Users className="w-3.5 h-3.5 mr-2" />
                    Officers
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                  <Trash2 className="w-3.5 h-3.5 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-sm text-muted-foreground font-mono">{business.email}</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/businesses/${business.id}/inbound_emails`}>
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                Emails
                {business.inbound_emails_count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 font-normal text-xs px-1.5 py-0">{business.inbound_emails_count}</Badge>
                )}
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/businesses/${business.id}/physical_mails`}>
                <Package className="w-3.5 h-3.5 mr-1.5" />
                Physical Mail
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/businesses/${business.id}/cap_table`}>
                <PieChart className="w-3.5 h-3.5 mr-1.5" />
                Cap Table
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/members`}>
                <Users className="w-3.5 h-3.5 mr-1.5" />
                Team
              </Link>
            </Button>
          </div>
        </div>

        {/* Get Started callout - shows actions for missing services */}
        {(() => {
          const missing: { label: string; description: string; href: string; icon: React.ReactNode }[] = []
          if (!business.formation) missing.push({ label: 'Register your business', description: 'Incorporate or register in a new jurisdiction', href: `${basePath}/formations/new`, icon: <Building2 className="w-4 h-4 text-muted-foreground" /> })
          if (business.compliance_filings.length === 0) missing.push({ label: 'Stay compliant', description: 'Set up annual returns, filings and renewals', href: `${basePath}/compliance_filings/new`, icon: <ClipboardCheck className="w-4 h-4 text-muted-foreground" /> })
          if (business.tax_registrations.length === 0) missing.push({ label: 'Register for tax', description: 'VAT, corporate tax or other registrations', href: `${basePath}/tax_registrations/new`, icon: <FileText className="w-4 h-4 text-muted-foreground" /> })
          if (business.registered_agents.length === 0) missing.push({ label: 'Get a registered agent', description: 'Statutory agent for your jurisdiction', href: `${basePath}/registered_agents/new`, icon: <Shield className="w-4 h-4 text-muted-foreground" /> })
          if (business.registered_addresses.length === 0) missing.push({ label: 'Get a registered address', description: 'Official business address for filings', href: `${basePath}/registered_addresses/new`, icon: <MapPin className="w-4 h-4 text-muted-foreground" /> })
          if (business.nominee_directors.length === 0) missing.push({ label: 'Appoint a nominee director', description: 'Local director for your company', href: `${basePath}/nominee_directors/new`, icon: <Users className="w-4 h-4 text-muted-foreground" /> })
          if (business.nominee_shareholders.length === 0) missing.push({ label: 'Appoint a nominee shareholder', description: 'Hold shares on your behalf', href: `${basePath}/nominee_shareholders/new`, icon: <Users className="w-4 h-4 text-muted-foreground" /> })
          if (missing.length === 0) return null
          return (
            <div className="mb-8">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Get Started</h2>
              <div className="rounded-lg border border-dashed bg-muted/30 divide-y divide-dashed">
              {missing.map(item => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    {item.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">{item.label}</div>
                    <div className="text-xs text-muted-foreground">{item.description}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </Link>
              ))}
              </div>
            </div>
          )
        })()}

        {/* Formation - only show when present */}
        {business.formation && (
          <div className="mb-8">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Formation</h2>
            <div className="border rounded-lg">
              <Link
                href={`${basePath}/formations/${business.formation.id}`}
                className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{business.formation.legal_name}</div>
                    {business.formation.entity_name && (
                      <div className="text-xs text-muted-foreground">{business.formation.entity_name}</div>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </Link>
            </div>
          </div>
        )}

        {/* Compliance Filings - only show when present */}
        {business.compliance_filings.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Compliance Filings</h2>
              <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                <Link href={`${basePath}/compliance_filings/new`}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Link>
              </Button>
            </div>
            <div className="border rounded-lg divide-y">
              {business.compliance_filings.map((cf) => (
                <Link
                  key={cf.id}
                  href={`${basePath}/compliance_filings/${cf.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <ClipboardCheck className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{cf.type_name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{cf.frequency.split('_').join('-')}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <StatusBadge status={cf.status} />
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Tax Registrations - only show when present */}
        {business.tax_registrations.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tax Registrations</h2>
              <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                <Link href={`${basePath}/tax_registrations/new`}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Link>
              </Button>
            </div>
            <div className="border rounded-lg divide-y">
              {business.tax_registrations.map((r) => (
                <Link
                  key={r.id}
                  href={`${basePath}/tax_registrations/${r.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{r.type_name}</div>
                      {r.jurisdiction_label && (
                        <div className="text-xs text-muted-foreground">{r.jurisdiction_label}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <StageBadge stage={r.stage} variant="formation" />
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Registered Agents - only show when present */}
        {business.registered_agents.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Registered Agents</h2>
              <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                <Link href={`${basePath}/registered_agents/new`}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Link>
              </Button>
            </div>
            <div className="border rounded-lg divide-y">
              {business.registered_agents.map((ra) => (
                <Link
                  key={ra.id}
                  href={`${basePath}/registered_agents/${ra.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Shield className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{ra.type_name}</div>
                      {ra.jurisdiction_label && (
                        <div className="text-xs text-muted-foreground">{ra.jurisdiction_label}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <StageBadge stage={ra.stage} variant="service" />
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Registered Addresses - only show when present */}
        {business.registered_addresses.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Registered Addresses</h2>
              <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                <Link href={`${basePath}/registered_addresses/new`}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Link>
              </Button>
            </div>
            <div className="border rounded-lg divide-y">
              {business.registered_addresses.map((ra) => (
                <Link
                  key={ra.id}
                  href={`${basePath}/registered_addresses/${ra.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{ra.type_name}</div>
                      {ra.jurisdiction_label && (
                        <div className="text-xs text-muted-foreground">{ra.jurisdiction_label}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <StageBadge stage={ra.stage} variant="service" />
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Nominee Directors - only show when present */}
        {business.nominee_directors.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nominee Directors</h2>
              <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                <Link href={`${basePath}/nominee_directors/new`}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Link>
              </Button>
            </div>
            <div className="border rounded-lg divide-y">
              {business.nominee_directors.map((nd) => (
                <Link
                  key={nd.id}
                  href={`${basePath}/nominee_directors/${nd.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{nd.type_name}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <StageBadge stage={nd.stage} variant="service" />
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Nominee Shareholders - only show when present */}
        {business.nominee_shareholders.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nominee Shareholders</h2>
              <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                <Link href={`${basePath}/nominee_shareholders/new`}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </Link>
              </Button>
            </div>
            <div className="border rounded-lg divide-y">
              {business.nominee_shareholders.map((ns) => (
                <Link
                  key={ns.id}
                  href={`${basePath}/nominee_shareholders/${ns.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{ns.type_name}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <StageBadge stage={ns.stage} variant="service" />
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Business Details */}
        <div className="mb-8">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Business Details</h2>
          <div className="border rounded-lg divide-y">
            {/* Address */}
            <div className="group flex items-start gap-4 px-4 py-3">
              <span className="text-xs text-muted-foreground w-28 shrink-0 pt-0.5">Address</span>
              <div className="flex-1 min-w-0">
                {business.addresses?.length > 0 ? (
                  <div className="space-y-2">
                    {business.addresses.map((addr: BusinessAddress) => (
                      <div key={addr.id} className="group/item flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {addr.label && <span className="text-xs text-muted-foreground mr-1.5">{addr.label}:</span>}
                          <span className="text-sm text-foreground">{addr.street_address_1}</span>
                          {addr.street_address_2 && <span className="text-sm text-muted-foreground">, {addr.street_address_2}</span>}
                          <span className="text-sm text-muted-foreground">, {addr.city}{addr.state_province && `, ${addr.state_province}`} {addr.postal_code} · {addr.country_code}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditAddress(addr); setAddressSheetOpen(true) }}>
                            <Pencil className="w-3 h-3 text-muted-foreground" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => { if (await confirm({ description: 'Remove this address?', confirmLabel: 'Remove', destructive: true })) router.delete(`${basePath}/businesses/${business.id}/addresses/${addr.id}`) }}>
                            <Trash2 className="w-3 h-3 text-muted-foreground" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditAddress(null); setAddressSheetOpen(true) }}>
                <Plus className="w-3 h-3 text-muted-foreground" />
              </Button>
            </div>

            {/* Contact Info */}
            <div className="group flex items-start gap-4 px-4 py-3">
              <span className="text-xs text-muted-foreground w-28 shrink-0 pt-0.5">Contact</span>
              <div className="flex-1 min-w-0">
                {business.contact_infos?.length > 0 ? (
                  <div className="space-y-2">
                    {business.contact_infos.map((ci: BusinessContactInfo) => (
                      <div key={ci.id} className="group/item flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {ci.label && <span className="text-xs text-muted-foreground mr-1.5">{ci.label}:</span>}
                          <span className="text-sm text-foreground">{ci.email}</span>
                          {ci.phone && <span className="text-sm text-muted-foreground"> · {ci.phone}</span>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditContactInfo(ci); setContactInfoSheetOpen(true) }}>
                            <Pencil className="w-3 h-3 text-muted-foreground" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => { if (await confirm({ description: 'Remove this contact?', confirmLabel: 'Remove', destructive: true })) router.delete(`${basePath}/businesses/${business.id}/contact_infos/${ci.id}`) }}>
                            <Trash2 className="w-3 h-3 text-muted-foreground" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditContactInfo(null); setContactInfoSheetOpen(true) }}>
                <Plus className="w-3 h-3 text-muted-foreground" />
              </Button>
            </div>

            {/* Countries */}
            <div className="group flex items-start gap-4 px-4 py-3">
              <span className="text-xs text-muted-foreground w-28 shrink-0 pt-0.5">Countries</span>
              <div className="flex-1 min-w-0">
                {business.country_operations?.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {business.country_operations.map(co => (
                      <Badge key={co.id} variant="secondary" className="font-normal text-xs">{co.country_name}</Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setCountryOpsSheetOpen(true)}>
                <Pencil className="w-3 h-3 text-muted-foreground" />
              </Button>
            </div>

            {/* Activities */}
            <div className="group flex items-start gap-4 px-4 py-3">
              <span className="text-xs text-muted-foreground w-28 shrink-0 pt-0.5">Activities</span>
              <div className="flex-1 min-w-0">
                {business.activities?.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {business.activities.map(act => (
                      <Badge key={act.id} variant="secondary" className="font-normal text-xs gap-1 pr-1">
                        {act.name}
                        <button
                          type="button"
                          className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                          onClick={async () => { if (await confirm({ description: 'Remove this activity?', confirmLabel: 'Remove', destructive: true })) router.delete(`${basePath}/businesses/${business.id}/activities/${act.id}`) }}
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditActivity(null); setActivitySheetOpen(true) }}>
                <Plus className="w-3 h-3 text-muted-foreground" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <BusinessAddressSheet
        open={addressSheetOpen}
        onOpenChange={setAddressSheetOpen}
        businessId={business.id}
        address={editAddress}
        basePath={basePath}
        countries={countries}
        states={states}
      />
      <BusinessContactInfoSheet
        open={contactInfoSheetOpen}
        onOpenChange={setContactInfoSheetOpen}
        businessId={business.id}
        contact={editContactInfo}
        basePath={basePath}
      />
      <BusinessCountryOperationsSheet
        open={countryOpsSheetOpen}
        onOpenChange={setCountryOpsSheetOpen}
        businessId={business.id}
        basePath={basePath}
        countries={countries}
        currentOperations={business.country_operations || []}
      />
      <BusinessActivitySheet
        open={activitySheetOpen}
        onOpenChange={setActivitySheetOpen}
        businessId={business.id}
        activity={editActivity}
        basePath={basePath}
      />
    </DashboardLayout>
  )
}
